import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import { TelegramSafeAreaProvider } from './components/layout/TelegramSafeAreaProvider'
import { TechnicalInfoProvider } from './lib/TechnicalInfoContext'
import { WorkspaceProvider } from './context/WorkspaceContext'
import { hasTelegramHost, initTelegramUi, rememberTelegramHost } from './telegram'
import { initializeTheme } from './lib/theme'
import { AuthProvider } from './context/AuthContext'

// PWA visitors load financial screens only after their session is verified.
// eslint-disable-next-line react-refresh/only-export-components -- root bootstrap is not a Fast Refresh component boundary
const App = lazy(() => import('./App.tsx'))

async function start() {
  // Telegram's launch parameters identify the host before its SDK is loaded.
  // A standalone PWA does not wait for a third-party Telegram script.
  if (hasTelegramHost()) {
    rememberTelegramHost()
    await new Promise<void>(resolve => {
      const sdk = document.createElement('script')
      const timer = window.setTimeout(resolve, 8000)
      sdk.src = 'https://telegram.org/js/telegram-web-app.js'; sdk.async = true
      sdk.onload = sdk.onerror = () => { window.clearTimeout(timer); resolve() }
      document.head.appendChild(sdk)
    })
  }
  initTelegramUi()
  initializeTheme()

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <BrowserRouter basename="/admin/app">
        <AuthProvider><WorkspaceProvider>
          <TechnicalInfoProvider>
            <TelegramSafeAreaProvider>
              <Suspense fallback={<div role="status" className="p-6 text-sm text-muted-foreground">Загружаем приложение…</div>}><App /></Suspense>
            </TelegramSafeAreaProvider>
          </TechnicalInfoProvider>
        </WorkspaceProvider></AuthProvider>
      </BrowserRouter>
    </StrictMode>,
  )
}
void start()

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => { void navigator.serviceWorker.register('/admin/app/sw.js', { scope: '/admin/app/', updateViaCache: 'none' }).catch(() => { /* Online mode remains available. */ }) })
}
